import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    MAX_EVENT_IDS_PER_REQUEST,
    type SavedEventIdsResponse,
} from '@/src/homepage/api/events/savedEventIdsConstants';
import { releaseFetch } from '@/lib/release/request';

const STALE_MS = 5 * 60 * 1000;
const EMPTY_SET = new Set<string>();

type SavedEventIdsQueryKey = readonly ['saved-event-ids', string];

type SavedEventIdsCache = {
    savedIds: Set<string>;
    fetchedIds: Set<string>;
    eventIdsSignature: string;
};

export type SavedEventIdsLookup = {
    savedEventIds: Set<string>;
    isSaveStateReady: boolean;
};

async function postSavedEventIds(eventIds: string[]): Promise<Set<string>> {
    const response = await releaseFetch('/home/api/events/saved-event-ids/', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventIds }),
    });

    if (!response.ok) {
        // Throw rather than returning an empty set: an empty set would be
        // cached as authoritative "nothing saved" for the full staleTime,
        // hiding events the user actually saved. Throwing surfaces the failure
        // so React Query keeps prior cache and marks the lookup failed.
        throw new Error(`Saved event ids request failed with status ${response.status}`);
    }

    const data = (await response.json()) as SavedEventIdsResponse;
    return new Set(data.savedEventIds);
}

export async function fetchSavedEventIds(eventIds: string[]): Promise<Set<string>> {
    if (eventIds.length === 0) {
        return new Set();
    }

    const merged = new Set<string>();
    for (let i = 0; i < eventIds.length; i += MAX_EVENT_IDS_PER_REQUEST) {
        const batch = eventIds.slice(i, i + MAX_EVENT_IDS_PER_REQUEST);
        const saved = await postSavedEventIds(batch);
        saved.forEach((id) => merged.add(id));
    }

    return merged;
}

function getEventIdsSignature(eventIds: string[]): string {
    return eventIds.join('\0');
}

async function resolveSavedEventIds(visibleEventIds: string[]): Promise<SavedEventIdsCache> {
    // Re-resolve the full visible set on every run instead of merging into a
    // previously-fetched set. The event card owns the heart toggle once mounted
    // and gives us no callback when the user saves/unsaves, so an append-only
    // cache would keep reporting an event as saved after the user unsaved it
    // (and re-apply that stale state to remounted/duplicate cards). Re-querying
    // the current visible set keeps the cache aligned with server truth; React
    // Query still dedupes concurrent fetches for this key and only refetches on
    // a meaningful trigger (visible-id change, staleness, or refocus).
    const savedIds = await fetchSavedEventIds(visibleEventIds);

    return {
        savedIds,
        fetchedIds: new Set(visibleEventIds),
        eventIdsSignature: getEventIdsSignature(visibleEventIds),
    };
}

export function useSavedEventIds(
    userId: string | undefined,
    eventIds: string[],
    enabled: boolean,
): SavedEventIdsLookup {
    const queryClient = useQueryClient();
    const queryKey = useMemo(
        () => ['saved-event-ids', userId ?? 'anonymous'] as SavedEventIdsQueryKey,
        [userId],
    );

    const eventIdsRef = useRef(eventIds);
    eventIdsRef.current = eventIds;

    const queryFn = useCallback(() => resolveSavedEventIds(eventIdsRef.current), []);

    const queryEnabled = enabled && !!userId && eventIds.length > 0;

    const {
        data: cache,
        isFetching,
        failureCount,
    } = useQuery({
        queryKey,
        queryFn,
        enabled: queryEnabled,
        staleTime: STALE_MS,
        retry: false,
    });

    const eventIdsSignature = useMemo(() => getEventIdsSignature(eventIds), [eventIds]);
    useEffect(() => {
        if (!queryEnabled) {
            return;
        }

        // Cancellation (on cleanup) and fetch errors are observed via the
        // useQuery error state below; swallow the rejection here so it does not
        // surface as an unhandled promise rejection.
        void queryClient
            .fetchQuery({ queryKey, queryFn, staleTime: 0, retry: false })
            .catch(() => undefined);

        return () => {
            void queryClient.cancelQueries({ queryKey });
        };
    }, [eventIdsSignature, queryEnabled, queryClient, queryKey, queryFn]);

    if (!enabled) {
        return { savedEventIds: EMPTY_SET, isSaveStateReady: true };
    }

    const hasSavedStateForVisibleEvents = cache?.eventIdsSignature === eventIdsSignature;

    // Ready once the lookup settles. The happy path is "the current visible id
    // list has a resolved saved-state"; we also treat a settled-but-failed
    // lookup (failureCount > 0 once we are no longer fetching) or one that
    // can't run (e.g. no userId) as ready, so a transient failure degrades to a
    // best-effort state instead of hiding the save action forever. When the
    // lookup failed for a new visible list, React Query can still hold cache
    // from the previous list; keep that stale set out of the returned value.
    const isSaveStateReady =
        !queryEnabled || (!isFetching && (hasSavedStateForVisibleEvents || failureCount > 0));

    return {
        savedEventIds: hasSavedStateForVisibleEvents ? cache.savedIds : EMPTY_SET,
        isSaveStateReady,
    };
}
